import { createSelector } from '@reduxjs/toolkit'
import each from 'lodash/each'
import defaultsDeep from 'lodash/defaultsDeep'
import pickBy from 'lodash/pickBy'
import selectDepth from './depth'
import edit from './edit'
import selectFocus from './focus'
import selectSelected from './selected'
import * as selectNodes from './nodes'
import * as selectEdges from './edges'
import selectPinned from './pinned'
import { selectShapes, selectShapesByEntity } from './shapes'

type GraphEdge = import('types/domain').GraphEdge
type GraphEdgeMap = import('types/domain').GraphEdgeMap
type GraphNode = import('types/domain').GraphNode
type GraphNodeMap = import('types/domain').GraphNodeMap
type NeighborhoodResult = import('types/domain').NeighborhoodResult
type NodeEdgeMap = import('types/domain').NodeEdgeMap
type PreviousNeighborhoodsMap = import('types/domain').PreviousNeighborhoodsMap
type GraphFocus = import('types/domain').GraphFocus
type GraphPinned = import('types/domain').GraphPinned
type GraphSelected = import('types/domain').GraphSelected
type RootState = import('types/state').RootState
type ShapesAllMap = import('types/shapes').ShapesAllMap
type ShapesByEntityMap = import('types/shapes').ShapesByEntityMap


const selectPreviousNeighborhoods = (state: RootState): PreviousNeighborhoodsMap => {
  return state.graph?.previousNeighborhoods || {}
}

interface EntityOverlay {
  id: string
  [key: string]: unknown
}

type InitialEntities = Record<string, Omit<EntityOverlay, 'id'>>

interface SkippedState {
  nodes: string[]
  edges: string[]
}

interface PreviousNeighborhoodInput {
  neighborhood?: NeighborhoodResult
}

interface EditState {
  isEditing: boolean
  save: boolean
}

class NeighborhoodSelector {
  allNodes: GraphNodeMap
  allEdges: GraphEdgeMap
  nodeEdgeMap: NodeEdgeMap
  cullingDepth: number
  initialEntities: InitialEntities
  predicate: (obj: GraphNode | GraphEdge) => boolean
  previousNeighborhoods: PreviousNeighborhoodsMap | PreviousNeighborhoodInput[]
  nodes: GraphNodeMap
  edges: GraphEdgeMap
  skipped: SkippedState
  depth: number
  size: number
  lastSize: number
  depthFromInitialById?: Record<string, number>

  constructor(
    allNodes: GraphNodeMap,
    allEdges: GraphEdgeMap,
    nodeEdgeMap: NodeEdgeMap,
    cullingDepth: number,
    initialEntities: InitialEntities,
    predicate?: (obj: GraphNode | GraphEdge) => boolean,
    previousNeighborhoods: PreviousNeighborhoodsMap | PreviousNeighborhoodInput[] = []
  ) {
    this.allNodes = allNodes
    this.allEdges = allEdges
    this.nodeEdgeMap = nodeEdgeMap
    this.cullingDepth = cullingDepth
    this.initialEntities = initialEntities
    this.predicate = predicate ?? (() => true)
    this.previousNeighborhoods = previousNeighborhoods
    this.nodes = {}
    this.edges = {}
    this.skipped = {
      nodes: [],
      edges: []
    }
    this.depth = 1
    this.size = 0
    this.lastSize = 0
    each(this.initialEntities, (obj: Omit<EntityOverlay, 'id'>, id: string) => {
      this.add({ ...obj, id })
    })
  }

  ensureDepthFromInitialById() {
    if (this.depthFromInitialById != null) {
      return this.depthFromInitialById
    }
    const depthById: Record<string, number> = {}
    const queue: string[] = []
    const enqueue = (id: string | null | undefined, depth: number) => {
      if (id == null) {
        return
      }
      if (depthById[id] != null) {
        return
      }
      depthById[id] = depth
      queue.push(id)
    }

    each(this.initialEntities, (_obj: EntityOverlay, id: string) => {
      enqueue(id, 1)
    })

    while (queue.length > 0) {
      const id = queue.shift()
      if (!id) {
        continue
      }
      const nextDepth = (depthById[id] || 1) + 1

      if (this.allNodes[id] != null) {
        each(this.nodeEdgeMap[id], (_meta: unknown, eId: string) => {
          const e = this.allEdges[eId]
          if (e == null) {
            return
          }
          enqueue(eId, nextDepth)
          enqueue(e.data?.from, nextDepth)
          enqueue(e.data?.to, nextDepth)
        })
      } else if (this.allEdges[id] != null) {
        const e = this.allEdges[id]
        enqueue(e.data?.from, nextDepth)
        enqueue(e.data?.to, nextDepth)
      }
    }

    this.depthFromInitialById = depthById
    return this.depthFromInitialById
  }

  getDepthFromInitial(id: string, fallbackDepth: number) {
    const depthById = this.ensureDepthFromInitialById()
    return depthById[id] || fallbackDepth
  }

  getNeighborhood(): NeighborhoodResult {
    return {
      nodes: this.nodes,
      edges: this.edges
    }
  }

  getSkipped() {
    return this.skipped
  }

  currentDepth() {
    return this.depth
  }

  add(obj: EntityOverlay) {
    if (this.allNodes[obj.id]) {
      this.addNode(obj)
    }
    if (this.allEdges[obj.id]) {
      this.addEdge(obj)
    }
  }

  addNode(node: EntityOverlay, depth = 0) {
    const currentNode = this.allNodes[node.id]
    if (!currentNode) {
      return
    }
    if (currentNode.deleted) {
      return
    }
    if (this.nodes[node.id]) {
      return
    }
    const nextNode = defaultsDeep({}, currentNode, node, {
      neighbors: {},
      depth: depth || this.currentDepth(),
      more: 0,
      moreEdges: {}
    })
    if (!this.predicate(nextNode)) {
      this.skipped.nodes.push(nextNode.id)
      return
    }
    this.nodes[nextNode.id] = nextNode
    this.size += 1
  }

  addEdge(edge: EntityOverlay, depth = 0) {
    const currentEdge = this.allEdges[edge.id]
    if (!currentEdge) {
      return
    }
    const fromId = currentEdge.data?.from
    const toId = currentEdge.data?.to
    if (!fromId || !toId) {
      return
    }
    const f = this.allNodes[fromId]
    const t = this.allNodes[toId]
    if (!(f != null && t != null)) {
      return
    }
    if (currentEdge.deleted || f.deleted || t.deleted) {
      return
    }
    if (this.edges[edge.id]) {
      return
    }
    const nextEdge = defaultsDeep({}, currentEdge, edge, {
      depth: depth || this.currentDepth()
    })
    if (!this.predicate(nextEdge)) {
      this.skipped.edges.push(nextEdge.id)
      return
    }
    this.edges[nextEdge.id] = nextEdge
  }

  traverse() {
    while (this.currentDepth() <= this.cullingDepth && (this.size === 0 || this.lastSize < this.size)) {
      this.addNextLayer()
    }
    this.addMissingEdges()
    this.includePreviousNeighborhoods()
    this.markMoreEdges()
    this.addMissingEdges()
    this.removeDanglingEdges()
    this.setNeighbors()
  }

  includePreviousNeighborhoods() {
    each(this.previousNeighborhoods, (snapshot: PreviousNeighborhoodInput) => {
      const prev = snapshot?.neighborhood
      each(prev?.nodes, (n: GraphNode, id: string) => {
        if (this.nodes[id]) {
          return
        }
        const depth = this.getDepthFromInitial(id, n.depth ?? this.currentDepth())
        this.addNode({ id: id, preserved: true }, depth)
      })
      each(prev?.edges, (e: GraphEdge, id: string) => {
        if (this.edges[id]) {
          return
        }
        const depth = this.getDepthFromInitial(id, e.depth ?? this.currentDepth())
        this.addEdge({ id: id, preserved: true }, depth)
      })
    })
  }

  markMoreEdges() {
    each(this.nodes, (n: GraphNode) => {
      if (n.depth === undefined || n.depth < this.currentDepth()) {
        return
      }
      n.moreEdges = {}
      const moreEdges = n.moreEdges
      each(this.nodeEdgeMap[n.id] || {}, (eId: string) => {
        const e = this.allEdges[eId]
        if (!e) {
          return
        }
        const fromNode = this.nodes[e.data.from]
        const toNode = this.nodes[e.data.to]
        if (this.allNodes[e.data.from] != null && this.allNodes[e.data.to] != null && (!fromNode || !toNode)) {
          moreEdges[eId] = true
        }
      })
      n.more = Object.keys(moreEdges).length
    })
  }

  addNextLayer() {
    this.lastSize = this.size
    this.depth = this.depth + 1
    each(this.nodes, (n: GraphNode) => {
      if (n.depth !== this.currentDepth() - 1) {
        return
      }
      each(this.nodeEdgeMap[n.id], (eId: string) => {
        const e = this.allEdges[eId]
        if (!e) {
          return
        }
        this.add({ id: eId })
        if (n.id === e.data.to) {
          this.add({ id: e.data.from })
        }
        if (n.id === e.data.from) {
          this.add({ id: e.data.to })
        }
      })
    })
    each(this.edges, (e: GraphEdge) => {
      if (e.depth !== this.currentDepth() - 1) {
        return
      }
      this.add({ id: e.data.to })
      this.add({ id: e.data.from })
    })
  }

  addMissingEdges() {
    each(this.nodes, (n: GraphNode) => {
      each(this.nodeEdgeMap[n.id] || {}, (eId: string) => {
        const e = this.allEdges[eId]
        if (!e) {
          return
        }
        if (!(this.nodes[e.data.to] && this.nodes[e.data.from])) {
          return
        }
        this.add({ id: eId })
      })
    })
  }

  removeDanglingEdges() {
    this.edges = pickBy(this.edges, (e: GraphEdge) => {
      return this.nodes[e.data?.from] && this.nodes[e.data?.to]
    })
  }

  setNeighbors() {
    each(this.edges, (e: GraphEdge) => {
      const toNode = this.nodes[e.data.to]
      const fromNode = this.nodes[e.data.from]
      if (!toNode || !fromNode) {
        return
      }
      toNode.neighbors ??= {}
      fromNode.neighbors ??= {}
      toNode.neighbors[e.data.from] = true
      fromNode.neighbors[e.data.to] = true
    })
  }

  includeShapeMembers(shapesAll: ShapesAllMap, shapesByEntity: ShapesByEntityMap) {
    const seen = new Set<string>()
    const entityIds = [...Object.keys(this.nodes), ...Object.keys(this.edges)]
    for (const entityId of entityIds) {
      const shapeIds = shapesByEntity[entityId]
      if (!shapeIds) continue
      for (const shapeId of shapeIds) {
        if (seen.has(shapeId)) continue
        seen.add(shapeId)
        const shape = shapesAll[shapeId]
        if (!shape || !shape.layout) continue
        for (const memberId of shape.entityIds) {
          this.add({ id: memberId })
        }
      }
    }
  }
}

export const selectNeighborhood = createSelector(
  selectFocus,
  selectPinned,
  selectSelected,
  selectNodes.all,
  selectEdges.all,
  selectEdges.byNodeMap,
  selectDepth,
  edit,
  selectPreviousNeighborhoods,
  selectShapes,
  selectShapesByEntity,
  (focus: GraphFocus, pinned: GraphPinned, selected: GraphSelected, nodes: GraphNodeMap, edges: GraphEdgeMap, nodeEdgeMap: NodeEdgeMap, cullingDepth: number, editState: EditState, previousNeighborhoods: PreviousNeighborhoodsMap, shapesAll: ShapesAllMap, shapesByEntity: ShapesByEntityMap) => {
    return neighborhoodSelectorFn(focus, pinned, selected, nodes, edges, nodeEdgeMap, cullingDepth, editState, previousNeighborhoods, shapesAll, shapesByEntity)
  }
)

export const selectNeighborhoodDeep = createSelector(
  selectFocus,
  selectPinned,
  selectSelected,
  selectNodes.all,
  selectEdges.all,
  selectEdges.byNodeMap,
  selectDepth,
  edit,
  selectPreviousNeighborhoods,
  selectShapes,
  selectShapesByEntity,
  (focus: GraphFocus, pinned: GraphPinned, selected: GraphSelected, nodes: GraphNodeMap, edges: GraphEdgeMap, nodeEdgeMap: NodeEdgeMap, _cullingDepth: number, editState: EditState, previousNeighborhoods: PreviousNeighborhoodsMap, shapesAll: ShapesAllMap, shapesByEntity: ShapesByEntityMap) => {
    return neighborhoodSelectorFn(focus, pinned, selected, nodes, edges, nodeEdgeMap, 10, editState, previousNeighborhoods, shapesAll, shapesByEntity)
  }
)

const neighborhoodSelectorFn = (
  focus: GraphFocus,
  pinned: GraphPinned,
  selected: GraphSelected,
  nodes: GraphNodeMap,
  edges: GraphEdgeMap,
  nodeEdgeMap: NodeEdgeMap,
  cullingDepth: number,
  editState: EditState,
  previousNeighborhoods: PreviousNeighborhoodsMap,
  shapesAll: ShapesAllMap,
  shapesByEntity: ShapesByEntityMap,
) => {
  const objs: InitialEntities = {}
  objs[focus.id] = {
    focused: true,
    editing: editState.isEditing
  }
  each(pinned.nodes, (meta: unknown, id: string) => {
    objs[id] = defaultsDeep(objs[id], {
      pinned: true,
      pinnedMeta: meta
    })
  })
  each(selected.nodes, (_meta: string, id: string) => {
    objs[id] = defaultsDeep(objs[id], {
      selected: true
    })
  })
  each(selected.edges, (id: string) => {
    objs[id] = defaultsDeep(objs[id], {
      selected: true
    })
  })
  const neighborhood = new NeighborhoodSelector(nodes, edges, nodeEdgeMap, cullingDepth, objs, undefined, previousNeighborhoods)
  neighborhood.traverse()
  neighborhood.includeShapeMembers(shapesAll, shapesByEntity)
  neighborhood.addMissingEdges()
  neighborhood.markMoreEdges()
  neighborhood.removeDanglingEdges()
  neighborhood.setNeighbors()
  return neighborhood.getNeighborhood()
}

export { NeighborhoodSelector }